// PointageRH.jsx - PARTIE 1/4 - AVEC STATUTS DE PONCTUALITÉ
import React, { useState, useEffect, useRef } from 'react';
import {
  Clock, User, CheckCircle, X, Send, History, Users,
  ChevronLeft, ChevronRight, Calendar, Search, Filter,
  Check, AlertCircle, Loader2, Fingerprint, Eye, LogOut,
  ArrowRight, ArrowLeft, Coffee, RotateCcw, AlertTriangle, XCircle, HelpCircle, Badge, CreditCard,
  Save
} from 'lucide-react';

import api from '../services/api';
import { Toast } from '../components/ui/Toast';
import CameraPopup from './CameraPopup';
import HistoriquePointages from './HistoriquePointages';
import { useRights } from '../contexts/RightsContext';
import { useLoader } from '../contexts/LoaderContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import GuideVueAdmin from './GuideVueAdmin';
import Pagination from '../components/ui/Pagination';
import { formatHeureLocale } from '../utils/timeUtils';
import { useAuth } from '../contexts/AuthContext';

const PointageRH = () => {
  const { company } = useAuth();
  const companyTimezone = company?.timezone || 'Africa/Brazzaville';
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    return sessionStorage.getItem('pointage_viewMode') || 'employee';
  });
  const [loading, setLoading] = useState(false);
  const [loadingParametres, setLoadingParametres] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [currentTime, setCurrentTime] = useState(new Date());


  // 🔥 Feature Flag Compta
  const { isEnabled: comptaEnabled } = useFeatureFlag('compta_module');


  // 🆕 ÉTATS POUR LES DROITS D'ACCÈS
  // DROITS D'ACCÈS DEPUIS LE CONTEXTE GLOBAL
  const { rights: globalRights, loading: loadingGlobalRights } = useRights();
  const { showLoader, hideLoader } = useLoader();
  const [userRights, setUserRights] = useState({
    attendance_employee: true,
    attendance_admin: true,
    attendance_history: true
  });
  const [loadingRights, setLoadingRights] = useState(false);


  // 🆕 DÉTECTION DU MODE KIOSQUE
  const [isKioskMode, setIsKioskMode] = useState(
    () => sessionStorage.getItem('kioskMode') === 'true'
  );

  // États pour vue admin
  const [employeesData, setEmployeesData] = useState([]);
  const [pointagesBatch, setPointagesBatch] = useState([]);
  const [employesAbsents, setEmployesAbsents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingData, setLoadingData] = useState(false);     // ✅ AJOUTER
  const [savingPointages, setSavingPointages] = useState(false);  // ✅ AJOUTER
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showGuideAdmin, setShowGuideAdmin] = useState(false);
  const departmentDropdownRef = useRef(null);
  const rfidTimeoutRef = useRef(null); // ✅ FIX — remplace window.rfidTimeout

  const [adminPage, setAdminPage] = useState(1);
  const [adminPagination, setAdminPagination] = useState({ total: 0, totalPages: 1 });

  // États pour vue employé
  const [badgeNumber, setBadgeNumber] = useState('');
  const [employeeIdentified, setEmployeeIdentified] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Etats pour la prise de photo
  const [showCameraPopup, setShowCameraPopup] = useState(false);
  const [pendingPointageData, setPendingPointageData] = useState(null);
  const [pendingEmployeeData, setPendingEmployeeData] = useState(null);




  // États pour les paramètres de plages horaires (avec pauses)
  const [parametresPointage, setParametresPointage] = useState(() => {
    try {
      const cached = localStorage.getItem('hp_parametres_pointage');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return {
      arrivee_debut: '06:00',
      arrivee_fin: '10:00',
      depart_debut: '15:00',
      depart_fin: '23:00',
      mode_pause: 'automatique',
      mode_pointage: null,
      pause_duree_auto: '01:00:00',
      pause_sortie_debut: '11:30',
      pause_sortie_fin: '14:00',
      pause_retour_debut: '12:00',
      pause_retour_fin: '15:00'
    };
  });

  // États pour historique
  const [historiqueSearchTerm, setHistoriqueSearchTerm] = useState('');
  const [histDepartmentFilter, setHistDepartmentFilter] = useState('');
  const [historiquePage, setHistoriquePage] = useState(1);
  const [historiquePagination, setHistoriquePagination] = useState({ total: 0, totalPages: 1 });
  const [pointagesHistorique, setPointagesHistorique] = useState([]);
  const [statsHistorique, setStatsHistorique] = useState({
    total_employes: 0,
    total_pointages: 0,
    arrivees: 0,
    departs: 0,
    pauses_sortie: 0,
    pauses_retour: 0,
    presents: 0,
    absents: 0,
    envoyes: 0
  });

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================

  const formatDateForAPI = (date) => {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatHeure = (heure) => {
    return heure.replace(':', 'h');
  };

  // Déterminer le type de pointage selon l'heure et le mode
  const determinerTypePointage = () => {
    const heures = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const heureActuelle = `${String(heures).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    // Plage d'arrivée
    if (heureActuelle >= parametresPointage.arrivee_debut && heureActuelle < parametresPointage.arrivee_fin) {
      return 'arrivee';
    }

    // Si mode pointage : vérifier les plages de pause
    if (parametresPointage.mode_pause === 'pointage') {
      // Plage sortie pause
      if (heureActuelle >= parametresPointage.pause_sortie_debut && heureActuelle < parametresPointage.pause_sortie_fin) {
        return 'pause_sortie';
      }

      // Plage retour pause
      if (heureActuelle >= parametresPointage.pause_retour_debut && heureActuelle < parametresPointage.pause_retour_fin) {
        return 'pause_retour';
      }
    }

    // Plage de départ
    if (heureActuelle >= parametresPointage.depart_debut && heureActuelle < parametresPointage.depart_fin) {
      return 'depart';
    }

    return null;
  };

  // Obtenir le libellé et l'icône selon le type
  const getPointageInfo = (type) => {
    const infos = {
      arrivee: {
        label: 'Arrivée',
        icon: Fingerprint,
        color: 'green',
        emoji: '',
        plage: `${formatHeure(parametresPointage.arrivee_debut)}-${formatHeure(parametresPointage.arrivee_fin)}`
      },
      pause_sortie: {
        label: 'Sortie Pause',
        icon: Fingerprint,
        color: 'orange',
        emoji: '',
        plage: `${formatHeure(parametresPointage.pause_sortie_debut)}-${formatHeure(parametresPointage.pause_sortie_fin)}`
      },
      pause_retour: {
        label: 'Retour Pause',
        icon: Fingerprint,
        color: 'purple',
        emoji: '',
        plage: `${formatHeure(parametresPointage.pause_retour_debut)}-${formatHeure(parametresPointage.pause_retour_fin)}`
      },
      depart: {
        label: 'Départ',
        icon: Fingerprint,
        color: 'blue',
        emoji: '',
        plage: `${formatHeure(parametresPointage.depart_debut)}-${formatHeure(parametresPointage.depart_fin)}`
      }
    };
    return infos[type] || infos.arrivee;
  };

  // ✅ NOUVELLE FONCTION : Obtenir les infos du statut de ponctualité
  const getStatutPonctualiteInfo = (statut) => {
    const statuts = {
      a_temps: {
        label: 'À temps',
        icon: CheckCircle,
        color: 'green',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-500'
      },

      tolerance: {
        label: 'Tolérance',
        icon: AlertTriangle,
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-500'
      },

      retard: {
        label: 'En retard',
        icon: Clock,
        color: 'orange',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-500'
      },

      retard_grave: {
        label: 'Retard grave',
        icon: XCircle,
        color: 'red',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-500'
      },

      depart_anticipe: {
        label: 'Départ anticipé',
        icon: AlertTriangle,
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-500'
      },


      depart_anticipe_grave: {
        label: 'Départ très anticipé',
        icon: XCircle,
        color: 'red',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-500'
      },

      pause_excessive: {
        label: 'Pause excessive',
        icon: XCircle,
        color: 'red',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-500'
      }
    };


    return statuts[statut] || {
      label: 'N/A',
      icon: HelpCircle,
      color: 'gray',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-500'
    };
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================


  useEffect(() => {
    const kioskMode = sessionStorage.getItem('kioskMode') === 'true';
    setIsKioskMode(kioskMode);
    console.log('🖥️ [Pointagerh] Mode kiosque:', kioskMode);
  }, []);


  // 🆕 CHARGEMENT DES DROITS AU MONTAGE
  useEffect(() => {
    if (loadingGlobalRights) return;

    const loadSubRights = async () => {
      try {
        const response = await api.moduleRights.getMyRights();
        if (response.success) {
          const newRights = {
            attendance_employee: response.rights.attendance_employee || false,
            attendance_admin: response.rights.attendance_admin || false,
            attendance_history: response.rights.attendance_history || false
          };
          setUserRights(newRights);

          // Définir viewMode par défaut UNIQUEMENT si pas de choix sauvegardé
          const savedMode = sessionStorage.getItem('pointage_viewMode');
          if (savedMode && (
            (savedMode === 'employee' && newRights.attendance_employee) ||
            (savedMode === 'admin' && newRights.attendance_admin) ||
            (savedMode === 'historique' && newRights.attendance_history)
          )) {
            // Mode sauvegardé valide, on le garde
          } else if (newRights.attendance_employee) {
            setViewMode('employee');
          } else if (newRights.attendance_admin) {
            setViewMode('admin');
          } else if (newRights.attendance_history) {
            setViewMode('historique');
          }
        }
      } catch (err) {
        console.error('❌ [Pointage] Erreur sous-droits:', err);
        setUserRights({ attendance_employee: true, attendance_admin: true, attendance_history: true });
      } finally {
        setLoadingRights(false);
      }
    };

    loadSubRights();
  }, [loadingGlobalRights]);



  useEffect(() => {
    sessionStorage.setItem('pointage_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);


  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  useEffect(() => {
    loadParametresPointage();
  }, []);

  useEffect(() => {
    setAdminPage(1);
    setHistoriquePage(1);
    loadData();
  }, [selectedDate, viewMode]);


  useEffect(() => {
    if (viewMode === 'admin') loadData();
  }, [adminPage]);


  useEffect(() => {
    if (viewMode === 'admin') {
      setAdminPage(1);
      loadData();
    }
  }, [searchTerm, departmentFilter]);


  useEffect(() => {
    if (viewMode === 'historique') loadData();
  }, [historiquePage]);


  // Fermer le dropdown département au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(e.target)) {
        setShowDepartmentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  useEffect(() => {
    console.log('🔍 [Historique] searchTerm changed:', historiqueSearchTerm);
    if (viewMode === 'historique') {
      setHistoriquePage(1);
      loadData({ searchOverride: historiqueSearchTerm, departmentOverride: histDepartmentFilter, pageOverride: 1 });
    }
  }, [historiqueSearchTerm, histDepartmentFilter]);

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================


  const loadParametresPointage = async () => {
    try {
      const cached = localStorage.getItem('hp_parametres_pointage');
      if (cached) {
        setParametresPointage(JSON.parse(cached));
      }
      const response = await api.parametresPointage.get();
      if (response.success) {
        setParametresPointage(response.data);
        localStorage.setItem('hp_parametres_pointage', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('❌ [loadParametresPointage] Erreur:', error);
    }
  };



  const loadData = async (overrides = {}) => {
    const {
      searchOverride = historiqueSearchTerm,
      departmentOverride = histDepartmentFilter,
      pageOverride = historiquePage,
    } = overrides;

    showLoader();
    setLoadingData(true);

    if (viewMode === 'admin') {
      setEmployeesData([]);
    } else if (viewMode === 'historique') {
      setPointagesHistorique([]);
    }

    try {
      if (viewMode === 'admin') {
        const employeesRes = await api.pointages.getEmployeesPourPointage(
          formatDateForAPI(selectedDate),
          adminPage,
          searchTerm,
          departmentFilter
        );
        if (employeesRes.success) {
          setEmployeesData(employeesRes.data);
          if (employeesRes.pagination) setAdminPagination(employeesRes.pagination);
        }
      } else if (viewMode === 'historique') {
        const historiqueRes = await api.pointages.getHistoriqueByDate(
          formatDateForAPI(selectedDate),
          pageOverride,
          searchOverride,
          departmentOverride
        );
        if (historiqueRes.success) {
          setPointagesHistorique(historiqueRes.data);
          setStatsHistorique(historiqueRes.stats);
          if (historiqueRes.pagination) setHistoriquePagination(historiqueRes.pagination);
        }
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
      showToast('Erreur de chargement', 'error');
    } finally {
      setLoadingData(false);
      hideLoader();
    }
  };



  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };


  // PointageRH.jsx - PARTIE 2/4 - AVEC STATUTS DE PONCTUALITÉ

  // ============================================================================
  // GESTION BADGE ET IDENTIFICATION
  // ============================================================================

  // ✅ FONCTION AMÉLIORÉE : Pointage automatique avec meilleurs messages d'erreur
  const handleAutoPointage = async (employeeData, photoBlob = null) => {
    const typePointage = determinerTypePointage();

    if (!typePointage) {
      showToast('Hors période de pointage', 'error');
      setTimeout(() => {
        handleLogoutEmployee();
      }, 3000);
      return;
    }

    try {
      const now = new Date();
      const pointageData = {
        employee_id: employeeData.id,
        date_pointage: formatDateForAPI(now),
        heure_pointage: now.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        statut: 'present'
      };

      let response;
      // 📸 Si photo fournie → endpoint avec photo
      if (photoBlob) {
        response = await api.pointages.enregistrerAvecPhoto(pointageData, photoBlob);
      } else {
        response = await api.pointages.enregistrerPointageEmployee(pointageData);
      }

      if (response.success) {
        const info = getPointageInfo(typePointage);
        showToast(
          `✓ ${info.label} enregistrée pour ${employeeData.first_name} ${employeeData.last_name}`,
          'success'
        );

        try {
          const updatedEmployee = await api.employees.verifyBadge(badgeNumber);
          if (updatedEmployee.success && updatedEmployee.data) {
            setEmployeeIdentified(updatedEmployee.data);
          }
        } catch (error) {
          console.error('Erreur rechargement employé:', error);
        }

        setTimeout(() => {
          handleLogoutEmployee();
        }, 3000);
      } else {
        showToast(response.message || 'Erreur lors du pointage', 'error');
        setTimeout(() => {
          handleLogoutEmployee();
        }, 4000);
      }
    } catch (error) {
      console.error('Erreur pointage auto:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors du pointage';
      showToast(errorMessage, 'error');
      setTimeout(() => {
        handleLogoutEmployee();
      }, 4000);
    }
  };





  // ✅ FONCTION MODIFIÉE : Vérification badge avec auto-pointage
  const handleVerifyBadge = async (autoPointage = false) => {
    if (!badgeNumber.trim()) {
      showToast('Veuillez scanner votre badge', 'error');
      return;
    }

    setVerificationLoading(true);
    try {
      const response = await api.employees.verifyBadge(badgeNumber);

      if (response.success && response.data) {
        setEmployeeIdentified(response.data);
        showToast(`Bienvenue ${response.data.first_name} ${response.data.last_name} !`, 'success');

        // Photo obligatoire → ouvrir popup caméra (mode RFID ET mode manuel)
        if (parametresPointage.photo_obligatoire) {
          setPendingEmployeeData(response.data);
          setShowCameraPopup(true);
        } else if (autoPointage) {
          // RFID sans photo → pointage direct
          handleAutoPointage(response.data);
        }
        // Manuel sans photo → l'employé clique sur le bouton fingerprint

      } else {
        showToast('Badge non reconnu', 'error');
        setBadgeNumber('');
      }
    } catch (error) {
      console.error('Erreur vérification badge:', error);
      showToast('Badge non reconnu', 'error');
      setBadgeNumber('');
    } finally {
      setVerificationLoading(false);
    }
  };


  const handleLogoutEmployee = () => {
    setEmployeeIdentified(null);
    setBadgeNumber('');
  };

  // ============================================================================
  // NAVIGATION DATES
  // ============================================================================

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // ============================================================================
  // POINTAGE EMPLOYÉ (VUE EMPLOYÉ)
  // ============================================================================

  const handleEmployeePointage = async (photoBlob = null) => {
    if (!employeeIdentified) {
      showToast('Veuillez d\'abord vous identifier', 'error');
      return;
    }

    const typePointage = determinerTypePointage();
    if (!typePointage) {
      const plages = parametresPointage.mode_pause === 'pointage'
        ? `Arrivée: ${formatHeure(parametresPointage.arrivee_debut)}-${formatHeure(parametresPointage.arrivee_fin)}, Sortie pause: ${formatHeure(parametresPointage.pause_sortie_debut)}-${formatHeure(parametresPointage.pause_sortie_fin)}, Retour pause: ${formatHeure(parametresPointage.pause_retour_debut)}-${formatHeure(parametresPointage.pause_retour_fin)}, Départ: ${formatHeure(parametresPointage.depart_debut)}-${formatHeure(parametresPointage.depart_fin)}`
        : `Arrivée: ${formatHeure(parametresPointage.arrivee_debut)}-${formatHeure(parametresPointage.arrivee_fin)}, Départ: ${formatHeure(parametresPointage.depart_debut)}-${formatHeure(parametresPointage.depart_fin)}`;

      showToast(`Pointage hors horaires. ${plages}`, 'error');
      return;
    }

    // 📸 Si photo obligatoire et pas encore de photo → ouvrir popup
    if (parametresPointage.photo_obligatoire && !photoBlob) {
      setPendingPointageData({ mode: 'manual' });
      setPendingEmployeeData(employeeIdentified);
      setShowCameraPopup(true);
      return;
    }

    setLoading(true);
    try {
      const now = new Date();
      const pointageData = {
        employee_id: employeeIdentified.id,
        date_pointage: formatDateForAPI(now),
        heure_pointage: now.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        statut: 'present'
      };

      let response;
      if (photoBlob) {
        response = await api.pointages.enregistrerAvecPhoto(pointageData, photoBlob);
      } else {
        response = await api.pointages.enregistrerPointageEmployee(pointageData);
      }

      if (response.success) {
        const info = getPointageInfo(typePointage);
        showToast(response.message || `${info.label} enregistrée avec succès !`, 'success');

        const updatedEmployee = await api.employees.verifyBadge(badgeNumber);
        if (updatedEmployee.success && updatedEmployee.data) {
          setEmployeeIdentified(updatedEmployee.data);
        }

        if (typePointage === 'depart') {
          setTimeout(() => {
            handleLogoutEmployee();
          }, 2000);
        }
      } else {
        showToast(response.message || 'Erreur lors du pointage', 'error');
      }
    } catch (error) {
      console.error('Erreur pointage employé:', error);
      showToast('Erreur lors du pointage', 'error');
    } finally {
      setLoading(false);
    }
  };



  // 📸 Callback quand la photo est prise dans le popup
  const handlePhotoCapture = (photoBlob) => {
    setShowCameraPopup(false);

    // Capturer les valeurs actuelles avant de les reset
    const currentPendingPointage = pendingPointageData;
    const currentPendingEmployee = pendingEmployeeData;

    setPendingPointageData(null);
    setPendingEmployeeData(null);

    if (currentPendingPointage?.mode === 'manual') {
      handleEmployeePointage(photoBlob);
    } else if (currentPendingEmployee) {
      handleAutoPointage(currentPendingEmployee, photoBlob);
    }
  };



  // 📸 Callback si l'utilisateur annule la photo
  const handleCameraClose = () => {
    setShowCameraPopup(false);
    setPendingPointageData(null);
    setPendingEmployeeData(null);
    showToast('Pointage annulé', 'error');
    setTimeout(() => {
      handleLogoutEmployee();
    }, 2000);
  };



  // ============================================================================
  // POINTAGE ADMIN (VUE ADMIN - LOT)
  // ============================================================================

  const handleSaveBatchPointages = async () => {
    if (pointagesBatch.length === 0) {
      showToast('Veuillez pointer au moins un employé', 'error');
      return;
    }

    // ✅ Protection anti-double-clic
    if (savingPointages) {
      return;
    }

    setSavingPointages(true);
    try {

      const pointagesData = pointagesBatch.map(pointage => ({
        employee_id: pointage.employee_id,
        date_pointage: formatDateForAPI(selectedDate),
        heure_pointage: pointage.type === 'absence' ? null : pointage.timestamp,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        statut: pointage.statut,
        observations: pointage.observations || '',
        type_pointage: pointage.type
      }));


      const response = await api.pointages.enregistrerLotEmployees({
        pointages: pointagesData
      });

      if (response.success) {


        showToast(`${pointagesBatch.length} pointage(s) enregistré(s) !`, 'success');
        setPointagesBatch([]);
        setEmployesAbsents([]);

        // Recharger uniquement les données admin en arrière-plan
        const employeesRes = await api.pointages.getEmployeesPourPointage(formatDateForAPI(selectedDate), adminPage);
        if (employeesRes.success) {
          setEmployeesData(employeesRes.data);
          if (employeesRes.pagination) setAdminPagination(employeesRes.pagination);
        }

      } else {
        showToast(response.message || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSavingPointages(false);
    }
  };


  const handleToggleAbsent = (employeeId) => {
    if (employesAbsents.includes(employeeId)) {
      // Retirer l'absence
      setEmployesAbsents(prev => prev.filter(id => id !== employeeId));
      setPointagesBatch(prev => prev.filter(p => !(p.employee_id === employeeId && p.type === 'absence')));
    } else {
      // Ajouter l'absence
      setEmployesAbsents(prev => [...prev, employeeId]);

      // Retirer tous les autres pointages pour cet employé
      setPointagesBatch(prev => prev.filter(p => p.employee_id !== employeeId));

      // Ajouter le pointage absence
      const now = new Date();
      setPointagesBatch(prev => [...prev, {
        employee_id: employeeId,
        timestamp: now.toISOString(),
        heure: now.toTimeString().slice(0, 5),
        statut: 'absent',
        type: 'absence',
        observations: null
      }]);
    }
  };

  // ============================================================================
  // ENVOI COMPTABILITÉ
  // ============================================================================

  const handleSendToAccounting = async (pointageId = null) => {
    try {
      if (pointageId) {
        const response = await api.pointages.envoyerComptabilite(pointageId);
        if (response.success) {
          showToast('Envoyé à la comptabilité', 'success');

          // Mise à jour locale — vue admin
          setEmployeesData(prev => prev.map(emp => {
            const updated = { ...emp };
            ['pointage_arrivee', 'pointage_pause_sortie', 'pointage_pause_retour', 'pointage_depart', 'pointage_absence'].forEach(key => {
              if (updated[key] && updated[key].id === pointageId) {
                updated[key] = { ...updated[key], statut_comptabilite: 'envoye' };
              }
            });
            return updated;
          }));

          // Mise à jour locale — historique
          setPointagesHistorique(prev => prev.map(p => {
            if (p.id === pointageId) {
              return { ...p, statut_comptabilite: 'envoye' };
            }
            return p;
          }));
        } else {
          showToast(response.message || "Erreur lors de l'envoi", 'error');
        }
      } else {
        // Envoi de tous les pointages du jour
        const response = await api.pointages.envoyerTousComptabilite(formatDateForAPI(selectedDate));
        if (response.success) {
          showToast('Tous les pointages envoyés', 'success');

          setEmployeesData(prev => prev.map(emp => {
            const updated = { ...emp };
            ['pointage_arrivee', 'pointage_pause_sortie', 'pointage_pause_retour', 'pointage_depart', 'pointage_absence'].forEach(key => {
              if (updated[key] && updated[key].statut_comptabilite === 'en_attente') {
                updated[key] = { ...updated[key], statut_comptabilite: 'envoye' };
              }
            });
            return updated;
          }));

          setPointagesHistorique(prev => prev.map(p => {
            if (p.statut_comptabilite === 'en_attente') {
              return { ...p, statut_comptabilite: 'envoye' };
            }
            return p;
          }));
        } else {
          showToast(response.message || "Erreur lors de l'envoi", 'error');
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
      showToast("Erreur lors de l'envoi", 'error');
    }
  };



  // Envoi groupé de pointages à la comptabilité
  const handleSendLotToAccounting = async (pointageIds) => {
    try {
      const response = await api.pointages.envoyerLotComptabilite(pointageIds);
      if (response.success) {
        showToast(`${response.count} pointage(s) envoyé(s) à la comptabilité`, 'success');

        // Mise à jour locale — vue admin
        setEmployeesData(prev => prev.map(emp => {
          const updated = { ...emp };
          ['pointage_arrivee', 'pointage_pause_sortie', 'pointage_pause_retour', 'pointage_depart', 'pointage_absence'].forEach(key => {
            if (updated[key] && pointageIds.includes(updated[key].id)) {
              updated[key] = { ...updated[key], statut_comptabilite: 'envoye' };
            }
          });
          return updated;
        }));

        // Mise à jour locale — historique
        setPointagesHistorique(prev => prev.map(p => {
          if (pointageIds.includes(p.id)) {
            return { ...p, statut_comptabilite: 'envoye' };
          }
          return;
        }));

      } else {
        showToast(response.message || "Erreur lors de l'envoi", 'error');
      }

    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || "Erreur lors de l'envoi", 'error');
    }

  };


  // ============================================================================
  // FILTRAGE EMPLOYÉS
  // ============================================================================

  const filteredEmployees = employeesData.filter(emp => {
    const matchesSearch = !searchTerm ||
      `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.badge_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = !departmentFilter || emp.department === departmentFilter;

    return matchesSearch && matchesDepartment;
  });

  const uniqueDepartments = [...new Set(employeesData.map(emp => emp.department).filter(Boolean))];

  // ============================================================================
  // VUE EMPLOYÉ - INTERFACE DE POINTAGE
  // ============================================================================

  // ============================================================================
  // VUE EMPLOYÉ - INTERFACE BADGEUSE RFID
  // ============================================================================

  // ============================================================================
  // VUE EMPLOYÉ - INTERFACE BADGEUSE RFID
  // ============================================================================

  // ============================================================================
  // VUE EMPLOYÉ - INTERFACE BADGEUSE RFID (STYLE PROFESSIONNEL)
  // ============================================================================

  // ============================================================================
  // VUE EMPLOYÉ - INTERFACE BADGEUSE RFID
  // ============================================================================

  const renderEmployeeViewRFID = () => {
    const typePointageActuel = determinerTypePointage();
    const infoPointage = typePointageActuel ? getPointageInfo(typePointageActuel) : null;

    return (
      <div className="space-y-6">


        {/* HORLOGE ET PÉRIODE ACTUELLE */}
        {!isKioskMode && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="text-6xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'monospace' }}>
              {currentTime.toLocaleTimeString('fr-FR')}
            </div>
            <div className="text-base text-gray-600 font-medium">
              {currentTime.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </div>

            {/* INDICATEUR DE PÉRIODE */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#448696]/20">
              <span className="text-sm font-semibold text-[#4f7f8a]">
                {infoPointage ? (
                  <span className="flex items-center gap-2">
                    <infoPointage.icon size={16} />
                    Période {infoPointage.label.toLowerCase()} ({infoPointage.plage})
                  </span>
                ) : (
                  'Hors période de pointage'
                )}
              </span>
            </div>
          </div>
        )}

        {/* IDENTIFICATION BADGE */}
        {!employeeIdentified && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8 md:p-12">
            <div className="px-3 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-8 rounded-xl flex items-center justify-center gap-2 sm:gap-3" style={{ backgroundColor: '#448696' }}>
              <CreditCard size={20} className="text-white sm:w-7 sm:h-7" />
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white">Identification par scan badge</h2>
            </div>

            <div className="max-w-md mx-auto space-y-6">
              {/* Animation main + carte RFID */}
              <div className="relative w-64 h-64 mx-auto mb-6">
                {/* Cercle de fond */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#448696]/20 to-[#6fa3b0]/20 border-4 border-[#448696]/30"></div>

                {/* Ondes radio animées */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="absolute w-32 h-32 rounded-full border-4 border-[#448696]/40 animate-ping"></div>
                  <div className="absolute w-24 h-24 rounded-full border-4 border-[#448696]/60 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                  <div className="absolute w-16 h-16 rounded-full border-4 border-[#448696]/80 animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                </div>

                {/* Main + Carte (avec animation de mouvement) */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative animate-float">
                    {/* Carte RFID */}
                    <div className="relative w-24 h-16 bg-white rounded-lg shadow-2xl border-2 border-gray-200 mb-4 transform rotate-12">
                      <div className="absolute top-2 left-2 w-8 h-6 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded"></div>
                      <div className="absolute bottom-2 right-2 text-[10px] font-bold text-gray-400">RFID</div>

                      {/* Ondes sur la carte */}
                      <div className="absolute -right-6 top-1/2 transform -translate-y-1/2">
                        <div className="flex gap-1">
                          <div className="w-1 h-3 bg-[#448696] rounded-full animate-pulse"></div>
                          <div className="w-1 h-4 bg-[#448696] rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-1 h-5 bg-[#448696] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-1 h-4 bg-[#448696] rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                        </div>
                      </div>
                    </div>

                    {/* Main stylisée */}
                    <svg width="80" height="80" viewBox="0 0 100 100" className="mx-auto transform -rotate-12">
                      <g fill="#374151">
                        {/* Paume */}
                        <ellipse cx="50" cy="65" rx="18" ry="25" />

                        {/* Pouce */}
                        <rect x="25" y="55" width="12" height="20" rx="6" transform="rotate(-30 31 65)" />

                        {/* Index */}
                        <rect x="38" y="30" width="10" height="25" rx="5" />

                        {/* Majeur */}
                        <rect x="50" y="25" width="10" height="30" rx="5" />

                        {/* Annulaire */}
                        <rect x="62" y="30" width="10" height="25" rx="5" />

                        {/* Auriculaire */}
                        <rect x="74" y="35" width="8" height="20" rx="4" />
                      </g>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Champ caché pour capture RFID */}
              <input
                ref={(input) => {
                  if (input && !employeeIdentified) {
                    input.focus();
                  }
                }}
                type="text"
                value=""
                onChange={() => { }}
                onKeyDown={(e) => {
                  if (e.keyCode === 16 || e.keyCode === 17 || e.keyCode === 18) return;

                  if (e.keyCode === 13 && badgeNumber.trim()) {
                    e.preventDefault();
                    handleVerifyBadge(true);
                    return;
                  }

                  let char = '';
                  if (e.keyCode >= 48 && e.keyCode <= 57) {
                    char = String.fromCharCode(e.keyCode);
                  } else if (e.keyCode >= 65 && e.keyCode <= 90) {
                    char = String.fromCharCode(e.keyCode);
                  }

                  if (char) {
                    setBadgeNumber(prev => prev + char);
                  }

                  e.preventDefault();
                }}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px'
                }}
              />

              {/* Texte d'instruction */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Approchez votre badge du lecteur
                </h3>
                <p className="text-sm text-gray-600">
                  Le système détectera automatiquement votre badge
                </p>
              </div>

              {/* Indicateur de badge détecté */}

              {/* Champ caché pour capture RFID UNIQUEMENT */}
              <input
                ref={(input) => {
                  if (input && !employeeIdentified) {
                    input.focus();
                  }
                }}
                type="text"
                value=""
                onChange={() => { }}
                onKeyDown={(e) => {
                  // Ignorer les touches de modification
                  if (e.keyCode === 16 || e.keyCode === 17 || e.keyCode === 18) return;

                  // Si c'est Enter avec un badge, vérifier
                  if (e.keyCode === 13 && badgeNumber.trim()) {
                    e.preventDefault();
                    handleVerifyBadge(true);
                    // Réinitialiser après vérification
                    setTimeout(() => setBadgeNumber(''), 100);
                    return;
                  }

                  // Ignorer tout ce qui n'est pas alphanumérique
                  if (!((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 90))) {
                    e.preventDefault();
                    return;
                  }

                  // Ajouter le caractère
                  const char = String.fromCharCode(e.keyCode);
                  setBadgeNumber(prev => prev + char);

                  // ✅ NOUVEAU : Auto-reset après 500ms d'inactivité (pour ignorer saisie manuelle)
                  if (rfidTimeoutRef.current) clearTimeout(rfidTimeoutRef.current);
                  rfidTimeoutRef.current = setTimeout(() => {
                    setBadgeNumber('');
                  }, 500);

                  e.preventDefault();
                }}
                onBlur={(e) => {
                  // ✅ Toujours refocus pour capturer le prochain scan
                  setTimeout(() => e.target.focus(), 100);
                }}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0
                }}
              />
            </div>
          </div>
        )}

        {/* INTERFACE DE POINTAGE (APRÈS IDENTIFICATION) */}
        {employeeIdentified && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
            <div className="px-3 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-8 rounded-xl flex items-center justify-center gap-2 sm:gap-3" style={{ backgroundColor: '#448696' }}>
              <CheckCircle size={20} className="text-white sm:w-7 sm:h-7" />
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white">Pointage en cours</h2>
            </div>


            {/* CARTE EMPLOYÉ */}
            <div className="mb-4 sm:mb-8 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm" style={{ borderColor: '#448696', backgroundColor: '#f0f9fb', border: '2px solid #448696' }}>
              <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-md flex-shrink-0"
                  style={{ backgroundColor: '#448696' }}>
                  <User className="text-white" size={24} />
                </div>
                <div className="text-left min-w-0">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {employeeIdentified.first_name} {employeeIdentified.last_name}
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-gray-600 mt-0.5 truncate">
                    {employeeIdentified.position} • {employeeIdentified.department}
                  </div>
                  <div className="text-xs font-medium text-gray-500 mt-0.5">
                    Badge: {employeeIdentified.badge_number}
                  </div>
                </div>
              </div>
            </div>

            {/* Message de pointage automatique */}
            <div className="mb-6 p-4 rounded-xl border-2" style={{ backgroundColor: '#fed77420', borderColor: '#fed774' }}>
              <div className="flex items-center justify-center gap-3">
                <Loader2 size={20} className="animate-spin" style={{ color: '#b08d1a' }} />
                <span className="text-sm font-semibold" style={{ color: '#8f7215' }}>
                  Enregistrement automatique du pointage...
                </span>
              </div>
            </div>



            {/* GRILLE DES POINTAGES V2 responsive */}
            <div className={`mb-6 grid gap-3 ${parametresPointage.mode_pause === 'pointage'
                ? 'grid-cols-2 md:grid-cols-4'
                : 'grid-cols-2'
              }`}>

              {/* ARRIVÉE */}

              <div className={`relative p-3 sm:p-5 rounded-xl sm:rounded-2xl border-2 overflow-hidden transition-all ${employeeIdentified.a_pointe_arrivee
                  ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 shadow-md shadow-green-100'
                  : 'border-gray-200 bg-gray-50/50'
                }`}>
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center ${employeeIdentified.a_pointe_arrivee ? 'bg-green-500' : 'bg-gray-200'
                    }`}>
                    <Fingerprint size={12} className="text-white" />
                  </div>
                  <span className="font-bold text-xs sm:text-sm text-gray-700">Arrivée</span>
                </div>
                {employeeIdentified.a_pointe_arrivee && employeeIdentified.pointage_arrivee ? (
                  <div>
                    <div className="text-xl sm:text-3xl font-black text-green-600 mb-1 sm:mb-2">
                      ✓ {formatHeureLocale(employeeIdentified.pointage_arrivee.heure_utc, companyTimezone)}
                    </div>


                    {employeeIdentified.pointage_arrivee.statut_ponctualite && (() => {
                      const info = getStatutPonctualiteInfo(employeeIdentified.pointage_arrivee.statut_ponctualite);
                      const StatutIcon = info.icon;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${info.bgColor} ${info.textColor}`}>
                          <StatutIcon size={11} /> {info.label}
                        </span>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
                    <span className="text-sm text-gray-400 font-medium">Non pointé</span>
                  </div>
                )}
              </div>

              {/* SORTIE PAUSE */}
              {parametresPointage.mode_pause === 'pointage' && (

                <div className={`relative p-3 sm:p-5 rounded-xl sm:rounded-2xl border-2 overflow-hidden transition-all ${employeeIdentified.a_pointe_pause_sortie
                    ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50 shadow-md shadow-orange-100'
                    : 'border-gray-200 bg-gray-50/50'
                  }`}>
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center ${employeeIdentified.a_pointe_pause_sortie ? 'bg-orange-500' : 'bg-gray-200'
                      }`}>
                      <Fingerprint size={12} className="text-white" />
                    </div>
                    <span className="font-bold text-xs sm:text-sm text-gray-700">Sortie Pause</span>
                  </div>
                  {employeeIdentified.a_pointe_pause_sortie && employeeIdentified.pointage_pause_sortie ? (
                    <div className="text-xl sm:text-3xl font-black text-orange-600">
                      ✓ {formatHeureLocale(employeeIdentified.pointage_pause_sortie.heure_utc, companyTimezone)}
                    </div>


                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
                      <span className="text-sm text-gray-400 font-medium">Non pointé</span>
                    </div>
                  )}
                </div>
              )}

              {/* RETOUR PAUSE */}
              {parametresPointage.mode_pause === 'pointage' && (

                <div className={`relative p-3 sm:p-5 rounded-xl sm:rounded-2xl border-2 overflow-hidden transition-all ${employeeIdentified.a_pointe_pause_retour
                    ? 'border-purple-400 bg-gradient-to-br from-purple-50 to-violet-50 shadow-md shadow-purple-100'
                    : 'border-gray-200 bg-gray-50/50'
                  }`}>
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center ${employeeIdentified.a_pointe_pause_retour ? 'bg-purple-500' : 'bg-gray-200'
                      }`}>
                      <Fingerprint size={12} className="text-white" />
                    </div>
                    <span className="font-bold text-xs sm:text-sm text-gray-700">Retour Pause</span>
                  </div>
                  {employeeIdentified.a_pointe_pause_retour && employeeIdentified.pointage_pause_retour ? (
                    <div>
                      <div className="text-xl sm:text-3xl font-black text-purple-600 mb-1 sm:mb-2">
                        ✓ {formatHeureLocale(employeeIdentified.pointage_pause_retour.heure_utc, companyTimezone)}
                      </div>

                      {employeeIdentified.pointage_pause_retour.statut_ponctualite && (() => {
                        const info = getStatutPonctualiteInfo(employeeIdentified.pointage_pause_retour.statut_ponctualite);
                        const StatutIcon = info.icon;
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${info.bgColor} ${info.textColor}`}>
                            <StatutIcon size={11} /> {info.label}
                          </span>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
                      <span className="text-sm text-gray-400 font-medium">Non pointé</span>
                    </div>
                  )}
                </div>
              )}

              {/* DÉPART */}
              <div className={`relative p-3 sm:p-5 rounded-xl sm:rounded-2xl border-2 overflow-hidden transition-all ${employeeIdentified.a_pointe_depart
                  ? 'border-blue-400 bg-gradient-to-br from-blue-50 to-sky-50 shadow-md shadow-blue-100'
                  : 'border-gray-200 bg-gray-50/50'
                }`}>
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center ${employeeIdentified.a_pointe_depart ? 'bg-blue-500' : 'bg-gray-200'
                    }`}>
                    <Fingerprint size={12} className="text-white" />
                  </div>
                  <span className="font-bold text-xs sm:text-sm text-gray-700">Départ</span>
                </div>
                {employeeIdentified.a_pointe_depart && employeeIdentified.pointage_depart ? (
                  <div>
                    <div className="text-xl sm:text-3xl font-black text-blue-600 mb-1 sm:mb-2">
                      ✓ {formatHeureLocale(employeeIdentified.pointage_depart.heure_utc, companyTimezone)}
                    </div>


                    {employeeIdentified.pointage_depart.statut_ponctualite && (() => {
                      const info = getStatutPonctualiteInfo(employeeIdentified.pointage_depart.statut_ponctualite);
                      const StatutIcon = info.icon;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${info.bgColor} ${info.textColor}`}>
                          <StatutIcon size={11} /> {info.label}
                        </span>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
                    <span className="text-sm text-gray-400 font-medium">Non pointé</span>
                  </div>
                )}
              </div>

            </div>



          </div>



        )}
      </div>
    );
  };



  const renderEmployeeViewManuel = () => {
    const typePointageActuel = determinerTypePointage();
    const infoPointage = typePointageActuel ? getPointageInfo(typePointageActuel) : null;

    return (
      <div className="space-y-6">
        {/* HORLOGE ET PÉRIODE ACTUELLE */}
        {!isKioskMode && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="text-6xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'monospace' }}>
              {currentTime.toLocaleTimeString('fr-FR')}
            </div>
            <div className="text-base text-gray-600 font-medium">
              {currentTime.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </div>

            {/* INDICATEUR DE PÉRIODE */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#448696]/20">


              <span className="text-sm font-semibold text-[#4f7f8a]">
                {infoPointage ? (
                  <span className="flex items-center gap-2">
                    <infoPointage.icon size={16} />
                    Période {infoPointage.label.toLowerCase()} ({infoPointage.plage})
                  </span>
                ) : (
                  'Hors période de pointage'
                )}
              </span>
            </div>
          </div>
        )}


        {/* IDENTIFICATION BADGE */}
        {!employeeIdentified && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8 md:p-12">
            <div className="px-3 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-8 rounded-xl flex items-center justify-center gap-2 sm:gap-3" style={{ backgroundColor: '#448696' }}>
              <Fingerprint size={20} className="text-white sm:w-7 sm:h-7" />
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white">Identification par saisie Badge</h2>
            </div>

            <div className="max-w-md mx-auto space-y-6">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-3">
                  Numéro de badge <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Votre numéro de badge"
                  value={badgeNumber}
                  onChange={(e) => setBadgeNumber(e.target.value.toUpperCase())}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && badgeNumber.trim()) {
                      handleVerifyBadge();
                    }
                  }}
                  className="w-full px-6 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none text-center text-xl font-bold transition-all"
                  style={{ borderColor: '#448696' }}
                  autoFocus
                />
              </div>

              <button
                onClick={handleVerifyBadge}
                disabled={!badgeNumber.trim() || verificationLoading}
                className="w-full py-4 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#448696' }}
              >
                {verificationLoading ? (
                  <>
                    <Loader2 className="w-6 h-6 inline mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <Check className="w-6 h-6 inline mr-2" />
                    Vérifier mon badge
                  </>
                )}
              </button>
            </div>
          </div>
        )}



        {/* INTERFACE DE POINTAGE (APRÈS IDENTIFICATION) */}
        {employeeIdentified && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
            <div className="px-3 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-8 rounded-xl flex items-center justify-center gap-2 sm:gap-3" style={{ backgroundColor: '#448696' }}>
              <CheckCircle size={20} className="text-white sm:w-7 sm:h-7" />
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white">Pointage</h2>
            </div>


            {/* CARTE EMPLOYÉ  MANUEL*/}
            <div className="mb-4 sm:mb-8 p-4 sm:p-6 rounded-xl sm:rounded-2xl relative shadow-sm" style={{ borderColor: '#448696', backgroundColor: '#f0f9fb', border: '2px solid #448696' }}>
              <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-md flex-shrink-0"
                  style={{ backgroundColor: '#448696' }}>
                  <User className="text-white" size={24} />
                </div>
                <div className="text-left min-w-0">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {employeeIdentified.first_name} {employeeIdentified.last_name}
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-gray-600 mt-0.5 truncate">
                    {employeeIdentified.position} • {employeeIdentified.department}
                  </div>
                  <div className="text-xs font-medium text-gray-500 mt-0.5">
                    Badge: {employeeIdentified.badge_number}
                  </div>
                </div>
              </div>

              {/* <button
                onClick={handleLogoutEmployee}
                className="absolute top-3 right-3 p-1.5 sm:p-2 rounded-lg bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-all shadow-sm"
                title="Changer d'employé"
              >
                <LogOut size={16} />
              </button> */}
            </div>


            {/* ✅ GRILLE DES POINTAGES responsive */}
            <div className={`mb-6 grid gap-3 ${parametresPointage.mode_pause === 'pointage'
                ? 'grid-cols-2 md:grid-cols-4'
                : 'grid-cols-2'
              }`}>

              <div className={`p-3 sm:p-4 rounded-xl border-2 ${employeeIdentified.a_pointe_arrivee
                  ? 'bg-green-50 border-green-500'
                  : 'bg-gray-50 border-gray-200'
                }`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Fingerprint size={16} className={employeeIdentified.a_pointe_arrivee ? 'text-green-600' : 'text-gray-400'} />
                  <span className="font-semibold text-xs sm:text-sm">Arrivée</span>
                </div>
                {employeeIdentified.a_pointe_arrivee && employeeIdentified.pointage_arrivee ? (
                  <div className="space-y-1.5">
                    <div className="text-lg sm:text-2xl font-bold text-green-600">
                      ✓ {formatHeureLocale(employeeIdentified.pointage_arrivee.heure_utc, companyTimezone)}
                    </div>


                    {/* ✅ NOUVEAU : Badge de statut de ponctualité */}


                    {employeeIdentified.pointage_arrivee.statut_ponctualite && (() => {
                      const StatutIcon = getStatutPonctualiteInfo(employeeIdentified.pointage_arrivee.statut_ponctualite).icon;
                      return (
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${getStatutPonctualiteInfo(employeeIdentified.pointage_arrivee.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employeeIdentified.pointage_arrivee.statut_ponctualite).textColor}`}>
                          <StatutIcon size={12} />
                          <span>{getStatutPonctualiteInfo(employeeIdentified.pointage_arrivee.statut_ponctualite).label}</span>
                        </div>
                      );
                    })()}


                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Non pointé</div>
                )}
              </div>


              {/* ✅ SORTIE PAUSE (si mode pointage) */}
              {parametresPointage.mode_pause === 'pointage' && (
                <div className={`p-3 sm:p-4 rounded-xl border-2 ${employeeIdentified.a_pointe_pause_sortie
                    ? 'bg-orange-50 border-orange-500'
                    : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Fingerprint size={16} className={employeeIdentified.a_pointe_pause_sortie ? 'text-orange-600' : 'text-gray-400'} />
                    <span className="font-semibold text-xs sm:text-sm">Sortie Pause</span>
                  </div>
                  {employeeIdentified.a_pointe_pause_sortie && employeeIdentified.pointage_pause_sortie ? (

                    <div className="text-lg sm:text-2xl font-bold text-orange-600">
                      ✓ {formatHeureLocale(employeeIdentified.pointage_pause_sortie.heure_utc, companyTimezone)}
                    </div>


                  ) : (
                    <div className="text-sm text-gray-500">Non pointé</div>
                  )}
                </div>
              )}

              {/* ✅ RETOUR PAUSE (si mode pointage) */}
              {parametresPointage.mode_pause === 'pointage' && (

                <div className={`p-3 sm:p-4 rounded-xl border-2 ${employeeIdentified.a_pointe_pause_retour
                    ? 'bg-purple-50 border-purple-500'
                    : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Fingerprint size={16} className={employeeIdentified.a_pointe_pause_retour ? 'text-purple-600' : 'text-gray-400'} />
                    <span className="font-semibold text-xs sm:text-sm">Retour Pause</span>
                  </div>
                  {employeeIdentified.a_pointe_pause_retour && employeeIdentified.pointage_pause_retour ? (
                    <div className="space-y-1.5">

                      <div className="text-lg sm:text-2xl font-bold text-purple-600">
                        ✓ {formatHeureLocale(employeeIdentified.pointage_pause_retour.heure_utc, companyTimezone)}
                      </div>

                      {/* ✅ NOUVEAU : Badge de statut de ponctualité pour la pause */}

                      {employeeIdentified.pointage_pause_retour.statut_ponctualite && (() => {
                        const StatutIcon = getStatutPonctualiteInfo(employeeIdentified.pointage_pause_retour.statut_ponctualite).icon;
                        return (
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${getStatutPonctualiteInfo(employeeIdentified.pointage_pause_retour.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employeeIdentified.pointage_pause_retour.statut_ponctualite).textColor}`}>
                            <StatutIcon size={12} />
                            <span>{getStatutPonctualiteInfo(employeeIdentified.pointage_pause_retour.statut_ponctualite).label}</span>
                          </div>
                        );
                      })()}


                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">Non pointé</div>
                  )}
                </div>
              )}

              {/* DÉPART */}
              <div className={`p-3 sm:p-4 rounded-xl border-2 ${employeeIdentified.a_pointe_depart
                  ? 'bg-blue-50 border-blue-500'
                  : 'bg-gray-50 border-gray-200'
                }`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Fingerprint size={16} className={employeeIdentified.a_pointe_depart ? 'text-blue-600' : 'text-gray-400'} />
                  <span className="font-semibold text-xs sm:text-sm">Départ</span>
                </div>
                {employeeIdentified.a_pointe_depart && employeeIdentified.pointage_depart ? (
                  <div className="space-y-1.5">

                    <div className="text-lg sm:text-2xl font-bold text-blue-600">
                      ✓ {formatHeureLocale(employeeIdentified.pointage_depart.heure_utc, companyTimezone)}
                    </div>


                    {/* ✅ NOUVEAU : Badge de statut de ponctualité */}
                    {employeeIdentified.pointage_depart.statut_ponctualite && (() => {
                      const StatutIcon = getStatutPonctualiteInfo(employeeIdentified.pointage_depart.statut_ponctualite).icon;
                      return (
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${getStatutPonctualiteInfo(employeeIdentified.pointage_depart.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employeeIdentified.pointage_depart.statut_ponctualite).textColor}`}>
                          <StatutIcon size={12} />
                          <span>{getStatutPonctualiteInfo(employeeIdentified.pointage_depart.statut_ponctualite).label}</span>
                        </div>
                      );
                    })()}

                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Non pointé</div>
                )}
              </div>
            </div>


            {/* BOUTON DE POINTAGE — masqué si photo obligatoire */}
            {!parametresPointage.photo_obligatoire && (
              <button
                onClick={handleEmployeePointage}
                disabled={loading || !typePointageActuel}
                className="w-40 h-40 sm:w-52 sm:h-52 lg:w-64 lg:h-64 mx-auto flex flex-col items-center justify-center text-white rounded-2xl sm:rounded-3xl transition-all transform hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#448696' }}
              >
                {loading ? (
                  <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 lg:w-24 lg:h-24 animate-spin" />
                ) : infoPointage ? (
                  <>
                    <infoPointage.icon className="w-12 h-12 sm:w-16 sm:h-16 lg:w-24 lg:h-24 mb-2 sm:mb-4" />
                    <span className="text-base sm:text-xl lg:text-2xl font-bold text-center px-2">
                      Pointer {infoPointage.label}
                    </span>
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-12 h-12 sm:w-16 sm:h-16 lg:w-24 lg:h-24 mb-2 sm:mb-4" />
                    <span className="text-sm sm:text-xl font-bold">Hors période</span>
                  </>
                )}
              </button>
            )}

            {/* AVERTISSEMENT HORS PÉRIODE */}
            {!typePointageActuel && (

              <div className="mt-6 p-4 rounded-xl bg-[#fed774]/30 border border-[#fed774]">
                <div className="flex items-center justify-center gap-2">
                  <AlertTriangle size={18} className="text-[#b08d1a]" />
                  <p className="text-sm font-medium text-[#8f7215] text-center">
                    Pointage disponible selon les plages configurées
                  </p>
                </div>

                {parametresPointage.mode_pause === 'pointage' && (
                  <p className="mt-2 text-xs text-center text-[#8f7215]">
                    (Mode avec pauses activé : 4 pointages requis)
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // VUE EMPLOYÉ - DISPATCHER (SÉLECTION DU MODE)
  // ============================================================================

  const renderEmployeeView = () => {
    // Tant que le mode n'est pas chargé, ne rien afficher
    if (!parametresPointage.mode_pointage) {
      return null;
    }

    if (parametresPointage.mode_pointage === 'rfid') {
      return renderEmployeeViewRFID();
    } else {
      return renderEmployeeViewManuel();
    }
  };



  // ============================================================================
  // VUE ADMIN - POINTAGE EN LOT
  // ============================================================================

  const renderAdminView = () => {
    const modePause = parametresPointage.mode_pause === 'pointage';

    return (
      <div className="space-y-6">
        {/* BARRE DE RECHERCHE ET FILTRES */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher un employé..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none font-medium text-sm transition-all"
              />
            </div>

            <div ref={departmentDropdownRef} className="relative">
              <button
                onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#448696] font-medium text-sm transition-all flex items-center justify-between"
              >
                <span className={departmentFilter ? 'text-gray-900' : 'text-gray-500'}>
                  {departmentFilter || 'Tous les départements'}
                </span>
                <Filter size={16} className="text-gray-400" />
              </button>



              {showDepartmentDropdown && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-40 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => {
                      setDepartmentFilter('');
                      setShowDepartmentDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50"
                  >
                    <span className="text-sm font-semibold text-gray-900">Tous les départements</span>
                  </button>
                  {uniqueDepartments.map((dept) => (
                    <button
                      key={dept}
                      onClick={() => {
                        setDepartmentFilter(dept);
                        setShowDepartmentDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 last:border-0"
                    >
                      <span className="text-sm font-semibold text-gray-900">{dept}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>


          </div>
        </div>


        {/* Bouton Guide */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Vue d'ensemble</h2>
          <button
            onClick={() => setShowGuideAdmin(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#b08d1a] hover:bg-[#b08d1a]/10 transition-all border border-gray-200 hover:border-[#b08d1a]/30"
          >
            <HelpCircle size={14} />
            Guide de lecture
          </button>
        </div>

        {/* HORLOGE */}



        {/* HORLOGE */}
        <div className="text-center p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'monospace' }}>
            {currentTime.toLocaleTimeString('fr-FR')}
          </div>
          <div className="text-sm font-medium text-gray-600 mt-1">
            {currentTime.toLocaleDateString('fr-FR')}
          </div>
          {modePause && (
            <div className="mt-2 text-xs font-semibold text-purple-600">
              Mode avec pauses activé (4 pointages)
            </div>
          )}
        </div>

        {/* ✅ TABLEAU DES EMPLOYÉS (2 ou 4 colonnes selon le mode) */}
        {!loadingData && <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
            <Users size={24} className="text-white" />
            <h3 className="font-bold text-white text-lg">
              Liste des employés ({filteredEmployees.length})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">

                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employé</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Département</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Arrivée</th>

                  {modePause && (
                    <>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Sortie Pause</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Retour Pause</th>
                    </>
                  )}

                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Départ</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Absent</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Observations</th>
                  <th className="px-4 py-3"></th>
                  {comptaEnabled && (
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Comptabilité</th>
                  )}
                </tr>

              </thead>
              <tbody className="divide-y divide-gray-100">

                {loadingData ? null : filteredEmployees.length === 0 ? (


                  <tr>
                    <td colSpan={modePause ? 10 : 8} className="px-4 py-8 text-center text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm font-medium">Aucun employé trouvé</p>
                    </td>
                  </tr>
                ) : (

                  filteredEmployees.map((employee) => {
                    const pointageArrivee = pointagesBatch.find(p => p.employee_id === employee.id && p.type === 'arrivee');
                    const pointagePauseSortie = pointagesBatch.find(p => p.employee_id === employee.id && p.type === 'pause_sortie');
                    const pointagePauseRetour = pointagesBatch.find(p => p.employee_id === employee.id && p.type === 'pause_retour');
                    const pointageDepart = pointagesBatch.find(p => p.employee_id === employee.id && p.type === 'depart');
                    const pointageAbsence = pointagesBatch.find(p => p.employee_id === employee.id && p.type === 'absence');

                    // ✅ VÉRIFIER SI L'EMPLOYÉ A UN POINTAGE ABSENCE EN BDD
                    const estAbsentEnBDD = employee.pointage_absence?.id;
                    const estAbsent = estAbsentEnBDD || employesAbsents.includes(employee.id);


                    return (
                      <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                        {/* EMPLOYÉ */}
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 shadow-sm"
                              style={{ backgroundColor: '#448696' }}>
                              <User className="text-white" size={18} />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {employee.first_name} {employee.last_name}
                              </div>
                              <div className="text-xs font-medium text-gray-600">
                                {employee.position}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* DÉPARTEMENT */}
                        <td className="px-4 py-3">
                          <span className="px-3 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700">
                            {employee.department || 'N/A'}
                          </span>
                        </td>





                        {/* ✅ ARRIVÉE AVEC STATUT */}
                        <td className="px-4 py-3">
                          {employee.a_pointe_arrivee && employee.pointage_arrivee ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">

                                <span className="px-3 py-1 text-xs font-semibold rounded-lg bg-green-100 text-green-700">
                                  ✓ {formatHeureLocale(employee.pointage_arrivee.heure_utc, companyTimezone)}
                                </span>

                                {employee.pointage_arrivee.statut_ponctualite && (() => {
                                  const StatutIcon = getStatutPonctualiteInfo(employee.pointage_arrivee.statut_ponctualite).icon;
                                  return (
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-md inline-flex items-center justify-center ${getStatutPonctualiteInfo(employee.pointage_arrivee.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employee.pointage_arrivee.statut_ponctualite).textColor}`}>
                                      <StatutIcon size={12} />
                                    </span>
                                  );
                                })()}
                              </div>

                            </div>
                          ) : !pointageArrivee ? (
                            <button
                              onClick={() => {
                                const now = new Date();
                                setPointagesBatch(prev => [...prev, {
                                  employee_id: employee.id,
                                  timestamp: now.toISOString(),
                                  heure: now.toTimeString().slice(0, 5),
                                  statut: 'present',
                                  type: 'arrivee'
                                }]);
                              }}
                              disabled={estAbsent}
                              className="px-3 py-1 text-white text-xs font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                              style={!estAbsent ? { backgroundColor: '#448696' } : {}}
                            >
                              <Fingerprint className="w-3 h-3 inline mr-1" />
                              Arrivée
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-green-100 text-green-700">
                                ✓ {pointageArrivee.heure}
                              </span>
                              <button
                                onClick={() => {
                                  setPointagesBatch(prev => prev.filter(p => !(p.employee_id === employee.id && p.type === 'arrivee')));
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>



                        {/* ✅ SORTIE PAUSE (si mode pointage) AVEC STATUT */}
                        {modePause && (
                          <td className="px-4 py-3">
                            {employee.a_pointe_pause_sortie && employee.pointage_pause_sortie ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">

                                  <span className="px-3 py-1 text-xs font-semibold rounded-lg bg-orange-100 text-orange-700">
                                    ✓ {formatHeureLocale(employee.pointage_pause_sortie.heure_utc, companyTimezone)}
                                  </span>

                                  {employee.pointage_pause_sortie.statut_ponctualite && (() => {
                                    const StatutIcon = getStatutPonctualiteInfo(employee.pointage_pause_sortie.statut_ponctualite).icon;
                                    return (
                                      <span className={`px-2 py-1 text-xs font-semibold rounded-md inline-flex items-center justify-center ${getStatutPonctualiteInfo(employee.pointage_pause_sortie.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employee.pointage_pause_sortie.statut_ponctualite).textColor}`}>
                                        <StatutIcon size={12} />
                                      </span>
                                    );
                                  })()}
                                </div>

                              </div>
                            ) : !pointagePauseSortie ? (
                              <button
                                onClick={() => {
                                  const now = new Date();
                                  setPointagesBatch(prev => [...prev, {
                                    employee_id: employee.id,
                                    timestamp: now.toISOString(),
                                    heure: now.toTimeString().slice(0, 5),
                                    statut: 'present',
                                    type: 'pause_sortie'
                                  }]);
                                }}
                                disabled={estAbsent}
                                className="px-3 py-1 text-white text-xs font-semibold rounded-lg bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                              >
                                <Fingerprint className="w-3 h-3 inline mr-1" />
                                Sortie
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-orange-100 text-orange-700">
                                  ✓ {pointagePauseSortie.heure}
                                </span>
                                <button
                                  onClick={() => {
                                    setPointagesBatch(prev => prev.filter(p => !(p.employee_id === employee.id && p.type === 'pause_sortie')));
                                  }}
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        )}



                        {/* ✅ RETOUR PAUSE (si mode pointage) AVEC STATUT */}
                        {modePause && (
                          <td className="px-4 py-3">
                            {employee.a_pointe_pause_retour && employee.pointage_pause_retour ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">

                                  <span className="px-3 py-1 text-xs font-semibold rounded-lg bg-purple-100 text-purple-700">
                                    ✓ {formatHeureLocale(employee.pointage_pause_retour.heure_utc, companyTimezone)}
                                  </span>

                                  {employee.pointage_pause_retour.statut_ponctualite && (() => {
                                    const StatutIcon = getStatutPonctualiteInfo(employee.pointage_pause_retour.statut_ponctualite).icon;
                                    return (
                                      <span className={`px-2 py-1 text-xs font-semibold rounded-md inline-flex items-center justify-center ${getStatutPonctualiteInfo(employee.pointage_pause_retour.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employee.pointage_pause_retour.statut_ponctualite).textColor}`}>
                                        <StatutIcon size={12} />
                                      </span>
                                    );
                                  })()}
                                </div>

                              </div>
                            ) : !pointagePauseRetour ? (
                              <button
                                onClick={() => {
                                  const now = new Date();
                                  setPointagesBatch(prev => [...prev, {
                                    employee_id: employee.id,
                                    timestamp: now.toISOString(),
                                    heure: now.toTimeString().slice(0, 5),
                                    statut: 'present',
                                    type: 'pause_retour'
                                  }]);
                                }}
                                disabled={estAbsent}
                                className="px-3 py-1 text-white text-xs font-semibold rounded-lg bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                              >
                                <Fingerprint className="w-3 h-3 inline mr-1" />
                                Retour
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-purple-100 text-purple-700">
                                  ✓ {pointagePauseRetour.heure}
                                </span>
                                <button
                                  onClick={() => {
                                    setPointagesBatch(prev => prev.filter(p => !(p.employee_id === employee.id && p.type === 'pause_retour')));
                                  }}
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        )}


                        {/* ✅ DÉPART AVEC STATUT */}
                        <td className="px-4 py-3">
                          {employee.a_pointe_depart && employee.pointage_depart ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">

                                <span className="px-3 py-1 text-xs font-semibold rounded-lg bg-blue-100 text-blue-700">
                                  ✓ {formatHeureLocale(employee.pointage_depart.heure_utc, companyTimezone)}
                                </span>

                                {employee.pointage_depart.statut_ponctualite && (() => {
                                  const StatutIcon = getStatutPonctualiteInfo(employee.pointage_depart.statut_ponctualite).icon;
                                  return (
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-md inline-flex items-center justify-center ${getStatutPonctualiteInfo(employee.pointage_depart.statut_ponctualite).bgColor} ${getStatutPonctualiteInfo(employee.pointage_depart.statut_ponctualite).textColor}`}>
                                      <StatutIcon size={12} />
                                    </span>
                                  );
                                })()}
                              </div>

                            </div>
                          ) : !pointageDepart ? (
                            <button
                              onClick={() => {
                                const now = new Date();
                                setPointagesBatch(prev => [...prev, {
                                  employee_id: employee.id,
                                  timestamp: now.toISOString(),
                                  heure: now.toTimeString().slice(0, 5),
                                  statut: 'present',
                                  type: 'depart'
                                }]);
                              }}
                              disabled={estAbsent}
                              className="px-3 py-1 text-white text-xs font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                              style={!estAbsent ? { backgroundColor: '#6fa3b0' } : {}}
                            >
                              <Fingerprint className="w-3 h-3 inline mr-1" />
                              Départ
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-100 text-blue-700">
                                ✓ {pointageDepart.heure}
                              </span>
                              <button
                                onClick={() => {
                                  setPointagesBatch(prev => prev.filter(p => !(p.employee_id === employee.id && p.type === 'depart')));
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>



                        {/* ✅ COLONNE ABSENT */}
                        <td className="px-4 py-3">
                          {estAbsentEnBDD ? (
                            <div className="space-y-2">
                              <span className="px-3 py-1 text-xs font-semibold rounded-lg bg-red-100 text-red-700">
                                ✗ Absent
                              </span>
                              {employee.pointage_absence?.statut_comptabilite === 'en_attente' && (
                                <button
                                  onClick={() => handleSendToAccounting(employee.pointage_absence.id)}
                                  disabled={loading}
                                  className="text-xs px-3 py-1 text-white rounded-lg font-semibold"
                                  style={{ backgroundColor: '#448696' }}
                                >
                                  <Send className="w-3 h-3 inline mr-1" />
                                  Envoyer
                                </button>
                              )}
                            </div>
                          ) : pointageAbsence ? (
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-red-100 text-red-700">
                                ✗ Absent
                              </span>
                              <button
                                onClick={() => handleToggleAbsent(employee.id)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleToggleAbsent(employee.id)}
                              disabled={employee.a_pointe_arrivee || employee.a_pointe_depart}
                              className="px-3 py-1 text-xs font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                              <X className="w-3 h-3 inline mr-1" />
                              Absent
                            </button>
                          )}
                        </td>


                        {/* OBSERVATIONS */}
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder="Observations..."
                            value={
                              // Observations depuis pointages en cours (batch)
                              pointageArrivee?.observations ||
                              pointagePauseSortie?.observations ||
                              pointagePauseRetour?.observations ||
                              pointageDepart?.observations ||
                              pointageAbsence?.observations ||
                              // Observations depuis pointages enregistrés en BDD
                              employee.pointage_arrivee?.observations ||
                              employee.pointage_pause_sortie?.observations ||
                              employee.pointage_pause_retour?.observations ||
                              employee.pointage_depart?.observations ||
                              employee.pointage_absence?.observations ||
                              ''
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              setPointagesBatch(prev => prev.map(p => {
                                if (p.employee_id === employee.id) {
                                  return { ...p, observations: value };
                                }
                                return p;
                              }));
                            }}
                            disabled={
                              // Désactivé si AUCUN pointage en cours (batch uniquement)
                              !pointageArrivee &&
                              !pointagePauseSortie &&
                              !pointagePauseRetour &&
                              !pointageDepart &&
                              !pointageAbsence
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-medium disabled:bg-gray-50 focus:outline-none transition-all"
                          />

                        </td>


                        {/* ENREGISTRER INDIVIDUEL — conditionnel */}
                        <td className="px-4 py-3">
                          {(pointageArrivee || pointagePauseSortie || pointagePauseRetour || pointageDepart || pointageAbsence) && (
                            <button
                              onClick={async () => {
                                const pointagesIndividuels = pointagesBatch
                                  .filter(p => p.employee_id === employee.id)
                                  .map(p => ({
                                    employee_id: p.employee_id,
                                    date_pointage: formatDateForAPI(selectedDate),
                                    heure_pointage: p.type === 'absence' ? null : p.timestamp,
                                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                    statut: p.statut,
                                    observations: p.observations || '',
                                    type_pointage: p.type
                                  }));

                                if (pointagesIndividuels.length === 0) return;

                                try {
                                  const response = await api.pointages.enregistrerLotEmployees({
                                    pointages: pointagesIndividuels
                                  });

                                  if (response.success) {
                                    showToast(`Pointage de ${employee.first_name} ${employee.last_name} enregistré`, 'success');
                                    // Retirer uniquement les pointages de cet employé du batch
                                    setPointagesBatch(prev => prev.filter(p => p.employee_id !== employee.id));
                                    setEmployesAbsents(prev => prev.filter(id => id !== employee.id));
                                    // Recharger les données
                                    const employeesRes = await api.pointages.getEmployeesPourPointage(formatDateForAPI(selectedDate));
                                    if (employeesRes.success) setEmployeesData(employeesRes.data);
                                  } else {
                                    showToast(response.message || 'Erreur lors de l\'enregistrement', 'error');
                                  }
                                } catch (error) {
                                  showToast('Erreur lors de l\'enregistrement', 'error');
                                }
                              }}

                              className="p-2 rounded-lg text-white transition-all shadow-sm hover:shadow-md"
                              style={{ backgroundColor: '#448696' }}
                            >
                              <Save size={16} />
                            </button>
                          )}
                        </td>

                        {/* COMPTABILITÉ — UN SEUL BOUTON PAR EMPLOYÉ */}
                        {/* 🔥 COMPTABILITÉ — Conditionnel (Compta Module) */}
                        {comptaEnabled && (
                          <td className="px-4 py-3">
                            {(() => {
                              const pointagesEnAttente = [
                                employee.pointage_arrivee,
                                employee.pointage_pause_sortie,
                                employee.pointage_pause_retour,
                                employee.pointage_depart,
                                employee.pointage_absence
                              ].filter(p => p && p.statut_comptabilite === 'en_attente');

                              const pointagesEnvoyes = [
                                employee.pointage_arrivee,
                                employee.pointage_pause_sortie,
                                employee.pointage_pause_retour,
                                employee.pointage_depart,
                                employee.pointage_absence
                              ].filter(p => p && p.statut_comptabilite === 'envoye');

                              if (pointagesEnAttente.length > 0) {
                                return (
                                  <button
                                    onClick={() => {
                                      const ids = pointagesEnAttente.map(p => p.id);
                                      handleSendLotToAccounting(ids);
                                    }}
                                    disabled={loading}
                                    className="text-xs px-3 py-1.5 text-white rounded-lg font-semibold transition-all shadow-sm hover:shadow flex items-center gap-1"
                                    style={{ backgroundColor: '#448696' }}
                                  >
                                    <Send size={12} />
                                    Envoyer ({pointagesEnAttente.length})
                                  </button>
                                );
                              } else if (pointagesEnvoyes.length > 0) {
                                return (
                                  <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-100 text-blue-700">
                                    ✓ Envoyé
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>

            </table>
          </div>


          {/* PAGINATION */}
          <Pagination
            page={adminPage}
            totalPages={adminPagination.totalPages}
            total={adminPagination.total}
            limit={10}
            onPageChange={(p) => {
              setPointagesBatch([]); // vider le batch au changement de page
              setAdminPage(p);
            }}
            itemLabel="employé"
          />


          {/* FOOTER AVEC ACTIONS */}
          <div className="flex justify-between items-center p-4 border-t border-gray-100 bg-gray-50">
            <div className="text-sm font-semibold text-gray-600">
              {pointagesBatch.length} pointage(s) en attente
            </div>


            <div className="flex gap-3">
              <button
                onClick={() => setPointagesBatch([])}
                className="px-6 py-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 font-semibold rounded-xl transition-all"
              >
                Réinitialiser
              </button>
              <button
                onClick={handleSaveBatchPointages}
                disabled={pointagesBatch.length === 0 || savingPointages}
                className="px-8 py-2 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#448696' }}
              >
                {savingPointages ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Enregistrement...
                  </span>
                ) : (
                  `Enregistrer (${pointagesBatch.length})`
                )}
              </button>
            </div>
          </div>
        </div>}
      </div>
    );
  };



  // ============================================================================
  // LAYOUT PRINCIPAL
  // ============================================================================

  // 🔍 LOGS DE DEBUG
  console.log('🖼️ [Pointage] RENDER - loadingRights:', loadingRights);
  console.log('🖼️ [Pointage] RENDER - userRights:', userRights);
  console.log('🖼️ [Pointage] RENDER - viewMode:', viewMode);

  // Attendre que les paramètres ET les droits soient chargés avant d'afficher
  const isReady = !loadingParametres && !loadingRights;


  return (
    <div className={`min-h-screen p-6 ${isKioskMode ? 'bg-white' : ''}`}
      style={{ backgroundColor: isKioskMode ? 'white' : '#fdfcf5' }}>
      {/* EN-TÊTE */}
      <div className="mb-8">
        {!isKioskMode && (
          <>
            <h1 className="text-4xl font-bold mb-2" style={{ color: "#448696" }}>
              Carnet de pointage
            </h1>
            <p className="text-gray-600 text-base font-medium">
              Suivi des présences et gestion des pointages du personnel
            </p>
          </>
        )}
      </div>


      {/* BARRE DE NAVIGATION */}
      {!isKioskMode && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* MODES DE VUE */}
            <div className="inline-flex gap-2 bg-gray-100 rounded-xl p-1.5">

              {/* Vue Employé */}
              {userRights.attendance_employee && (
                <button
                  onClick={() => setViewMode('employee')}
                  className={`flex items-center gap-2 px-6 py-3 font-semibold text-sm transition-all duration-200 rounded-lg ${viewMode === 'employee' ? 'text-white shadow-md' : 'text-gray-600'
                    }`}
                  style={{ backgroundColor: viewMode === 'employee' ? '#448696' : 'transparent' }}
                >
                  <Fingerprint size={18} />
                  Vue Employé
                </button>
              )}

              {/* Vue Admin */}
              {userRights.attendance_admin && (
                <button
                  onClick={() => setViewMode('admin')}
                  className={`flex items-center gap-2 px-6 py-3 font-semibold text-sm transition-all duration-200 rounded-lg ${viewMode === 'admin' ? 'text-gray-900 shadow-md' : 'text-gray-600'
                    }`}
                  style={{ backgroundColor: viewMode === 'admin' ? '#fed774' : 'transparent' }}
                >
                  <Users size={18} />
                  Vue Admin
                </button>
              )}

              {/* Vue Historique */}
              {userRights.attendance_history && (
                <button
                  onClick={() => setViewMode('historique')}
                  className={`flex items-center gap-2 px-6 py-3 font-semibold text-sm transition-all duration-200 rounded-lg ${viewMode === 'historique' ? 'text-white shadow-md' : 'text-gray-600'
                    }`}
                  style={{ backgroundColor: viewMode === 'historique' ? '#448696' : 'transparent' }}
                >
                  <History size={18} />
                  Historique
                </button>
              )}
            </div>

            {/* SÉLECTEUR DE DATE */}
            {(userRights.attendance_admin || userRights.attendance_history) && viewMode !== 'employee' && (
              <div className="flex items-center space-x-2">
                <button onClick={goToPreviousDay} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors font-medium"
                  >
                    <Calendar className="h-4 w-4 mr-2" style={{ color: '#448696' }} />
                    <span className="text-sm font-semibold">
                      {formatDateForDisplay(selectedDate)}
                    </span>
                  </button>

                  {showCalendar && (
                    <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-lg border border-gray-100 z-50 p-4">
                      <input
                        type="date"
                        value={formatDateForAPI(selectedDate)}
                        onChange={(e) => {
                          const newDate = new Date(e.target.value + 'T12:00:00');
                          setSelectedDate(newDate);
                          setShowCalendar(false);
                        }}
                        className="p-2 border border-gray-200 rounded-lg"
                      />
                    </div>
                  )}
                </div>

                <button onClick={goToNextDay} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={goToToday}
                  className="px-3 py-2 text-xs text-white rounded-lg font-semibold shadow-sm hover:shadow transition-all"
                  style={{ backgroundColor: '#448696' }}
                >
                  Aujourd'hui
                </button>
              </div>
            )}
          </div>
        </div>
      )}




      {/* CONTENU SELON LA VUE */}
      {!isReady ? null : (
        <div>
          {viewMode === 'employee' && renderEmployeeView()}
          {viewMode === 'admin' && renderAdminView()}
          {viewMode === 'historique' && (


            <HistoriquePointages
              pointagesHistorique={pointagesHistorique}
              statsHistorique={statsHistorique}
              onSendToAccounting={handleSendToAccounting}
              onSendLotToAccounting={handleSendLotToAccounting}
              loading={loading}
              loadingData={loadingData}
              selectedDate={formatDateForAPI(selectedDate)}
              showToast={showToast}
              modePause={parametresPointage.mode_pause}
              paginationInfo={historiquePagination}
              pageCourante={historiquePage}
              onPageChange={(p) => setHistoriquePage(p)}
              onSearchChange={setHistoriqueSearchTerm}
              onDepartmentChange={setHistDepartmentFilter}
            />
          )}


        </div>
      )}

      {/* TOAST NOTIFICATIONS */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}


      {/* 📸 POPUP CAMÉRA ANTI-FRAUDE */}
      <CameraPopup
        isOpen={showCameraPopup}
        onClose={handleCameraClose}
        onCapture={handlePhotoCapture}
        employeeName={pendingEmployeeData ? `${pendingEmployeeData.first_name} ${pendingEmployeeData.last_name}` : ''}
      />



      {showGuideAdmin && (
        <GuideVueAdmin
          onClose={() => setShowGuideAdmin(false)}
          modePause={parametresPointage.mode_pause}
          comptaEnabled={comptaEnabled}
        />
      )}


    </div>
  );
};

export default PointageRH;